/**
 * Shown on a property while the backend gathers its public records, listing
 * photos and price after it was added. The page refetches on its own (see
 * useProperty), so this disappears and the details fill in without a reload.
 */

import { useEffect, useState } from 'react';
import { Box, LinearProgress, Stack, Typography } from '@mui/material';

import { productPhotos } from '@/utils/propertyEnrichment';
import type { Product } from '@/types/ProductType';

type StepState = 'done' | 'active' | 'waiting';

const STEP_ICON: Record<StepState, string> = {
  done: 'fa-solid fa-circle-check',
  active: 'fa-solid fa-spinner fa-spin',
  waiting: 'fa-regular fa-circle',
};

/** Typical run length; the bar eases toward, but never reaches, 100%. */
const TYPICAL_MS = 90 * 1000;

export const PropertyEnrichmentProgress = ({
  product,
}: {
  product?: Product;
}) => {
  const start = new Date(
    product?.enrichment?.requestedAt ?? product?.createdAt ?? Date.now()
  ).getTime();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const elapsed = Math.max(0, now - start);
  const progress = Math.min(95, 100 * (1 - Math.exp(-elapsed / TYPICAL_MS)));
  const hasPhotos = productPhotos(product).length > 0;
  const hasFacts = Boolean(
    product?.bedrooms || product?.sqft || product?.enrichment?.parcelId
  );

  const steps: { label: string; state: StepState }[] = [
    { label: 'Address saved', state: 'done' },
    {
      label: 'Searching public records',
      state: hasFacts ? 'done' : 'active',
    },
    {
      label: 'Finding listing photos and price',
      state: hasPhotos ? 'done' : hasFacts ? 'active' : 'waiting',
    },
  ];

  return (
    <Box
      role="status"
      aria-live="polite"
      className="taste-panel"
      sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Gathering property details
        </Typography>
        <Typography variant="body2" color="text.secondary">
          We&apos;re pulling public records, listing photos and the asking
          price. This usually takes a minute or two — you can keep working, and
          this page updates on its own.
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{ height: 6, borderRadius: 3 }}
        aria-label="Property details progress"
      />
      <Stack gap={0.75}>
        {steps.map((step) => (
          <Stack
            key={step.label}
            direction="row"
            alignItems="center"
            gap={1.25}
            sx={{
              color:
                step.state === 'waiting' ? 'text.disabled' : 'text.primary',
            }}
          >
            <Box
              component="i"
              className={STEP_ICON[step.state]}
              sx={{
                width: 16,
                textAlign: 'center',
                color: step.state === 'done' ? 'success.main' : 'inherit',
              }}
              aria-hidden
            />
            <Typography variant="body2">{step.label}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};
